import { useMemo } from "react";
import { ITheme } from "@swiggy-private/dls-web-common/dist/components/types";
import { generateCustomTheme, ThemeOverrideResolver, ThemeOverrides } from "../helpers/CustomThemeBuilder";
import { useTheme } from "styled-components";

export type { ThemeOverrideResolver };

export type FlowThemeOverrideMap<TFlow extends string> = Record<TFlow, ThemeOverrideResolver>;

export type UseFlowThemeOverrideParams<TFlow extends string> = {
    flow: TFlow;
    overridesByFlow: FlowThemeOverrideMap<TFlow>;
};

export type FlowThemeOverrideResult = {
    customTheme: ITheme;
    cssVariables: Record<string, string>;
    baseTheme: ITheme;
};

const resolveOverrides = (resolver: ThemeOverrideResolver, theme: ITheme): ThemeOverrides => {
    return typeof resolver === "function" ? resolver(theme) : resolver;
};

/**
 * Resolves a custom theme + CSS variables for the active flow.
 * Scalable: add a new key to `overridesByFlow` and pass that flow — no PayButton changes needed.
 */
export const useFlowThemeOverride = <TFlow extends string>({
    flow,
    overridesByFlow,
}: UseFlowThemeOverrideParams<TFlow>): FlowThemeOverrideResult => {
    const theme = useTheme() as ITheme;

    return useMemo(() => {
        const overrides = resolveOverrides(overridesByFlow[flow], theme);
        const [customTheme, cssVariables] = generateCustomTheme(theme, overrides);

        return {
            customTheme,
            cssVariables,
            baseTheme: theme,
        };
    }, [flow, overridesByFlow, theme]);
};
